import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Building2, Users, Link as LinkIcon, MapPin, BarChart3, LogOut,
  Plus, ArrowRight, Power, PauseCircle, Trash2, AlertCircle, RefreshCcw,
  Sparkles, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PlatformPage = () => {
  const navigate = useNavigate();
  const { user, logout, enterTenant } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/platform/tenants`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/platform/stats`, { withCredentials: true }),
      ]);
      setTenants(t.data || []);
      setStats(s.data);
    } catch (err) {
      toast.error('Erro ao carregar dados da plataforma');
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = async (tenant) => {
    const res = await enterTenant(tenant.id);
    if (res.success) {
      toast.success(`Entrando em ${tenant.name}...`);
      navigate('/dashboard');
    } else {
      toast.error(res.error || 'Erro ao entrar no cliente');
    }
  };

  const [reprocessing, setReprocessing] = useState({ all: false, tenants: {} });
  const [seedingDemo, setSeedingDemo] = useState(false);

  const handleSeedDemo = async () => {
    if (!window.confirm('Criar novo cliente "Demo - Goiânia" com 95 lideranças e centenas de apoiadores fictícios?')) return;
    setSeedingDemo(true);
    const toastId = toast.loading('Gerando dados demo... (pode levar alguns segundos)');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/platform/seed-demo`,
        null,
        { withCredentials: true, timeout: 120000 }
      );
      toast.success(
        `Demo criado: ${data.leaders} lideranças, ${data.supporters} apoiadores, ${data.touches} toques.`,
        { id: toastId }
      );
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao gerar dados demo', { id: toastId });
    } finally {
      setSeedingDemo(false);
    }
  };

  const handleReprocessGeo = async (tenant) => {
    const key = tenant?.id || 'all';
    setReprocessing((prev) => ({
      ...prev,
      ...(key === 'all'
        ? { all: true }
        : { tenants: { ...prev.tenants, [key]: true } }),
    }));
    const label = tenant ? tenant.name : 'todos os clientes';
    const toastId = toast.loading(`Reprocessando geolocalizações de ${label}...`);
    try {
      const params = tenant ? { tenant_id: tenant.id } : {};
      const { data } = await axios.post(
        `${BACKEND_URL}/api/platform/reprocess-geolocations`,
        null,
        { params, withCredentials: true, timeout: 300000 }
      );
      toast.success(
        `Geo reprocessada: ${data.updated} atualizado(s), ${data.skipped} ignorado(s) de ${data.total}.`,
        { id: toastId }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao reprocessar', { id: toastId });
    } finally {
      setReprocessing((prev) => ({
        ...prev,
        ...(key === 'all'
          ? { all: false }
          : { tenants: { ...prev.tenants, [key]: false } }),
      }));
    }
  };

  const handleToggleStatus = async (tenant) => {
    const newStatus = tenant.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(
        `${BACKEND_URL}/api/platform/tenants/${tenant.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(newStatus === 'active' ? 'Cliente ativado' : 'Cliente pausado');
      fetchAll();
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleToggleInsights = async (tenant) => {
    const next = !tenant.insights_enabled;
    try {
      await axios.put(
        `${BACKEND_URL}/api/platform/tenants/${tenant.id}`,
        { insights_enabled: next },
        { withCredentials: true }
      );
      toast.success(next ? 'Insights habilitado' : 'Insights desabilitado');
      fetchAll();
    } catch (err) {
      toast.error('Erro ao atualizar módulo Insights');
    }
  };

  const handleDelete = async (tenant) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/platform/tenants/${tenant.id}`, {
        withCredentials: true,
      });
      toast.success('Cliente excluído');
      fetchAll();
    } catch (err) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const statusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Ativo</Badge>;
    if (status === 'paused') return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pausado</Badge>;
    return <Badge className="bg-rose-100 text-rose-700 border-rose-200">Cancelado</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/meadd-logo.png" alt="MeAdd" className="h-[4.25rem]" />
            <div className="ml-2 pl-3 border-l border-slate-200">
              <p className="text-xs text-slate-500 leading-tight">Painel da plataforma</p>
              <p className="text-sm font-semibold leading-tight text-slate-900">Super Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="platform-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Building2} label="Clientes ativos" value={stats?.active_tenants ?? 0} total={stats?.total_tenants} accent="indigo" />
          <StatCard icon={Users} label="Lideranças" value={stats?.total_leaders ?? 0} accent="emerald" />
          <StatCard icon={LinkIcon} label="Links criados" value={stats?.total_links ?? 0} accent="sky" />
          <StatCard icon={BarChart3} label="Redirecionamentos" value={stats?.total_redirecionamentos ?? 0} accent="amber" />
        </div>

        {/* Tenants table */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-xl">Clientes</CardTitle>
              <CardDescription>Cada cliente é uma instância isolada do sistema.</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleSeedDemo}
                disabled={seedingDemo}
                data-testid="seed-demo-btn"
                title="Cria um cliente Demo - Goiânia com 95 lideranças e ~600 apoiadores fictícios"
              >
                <Sparkles className={`h-4 w-4 mr-2 ${seedingDemo ? 'animate-spin' : ''}`} />
                {seedingDemo ? 'Gerando...' : 'Gerar demo Goiânia'}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleReprocessGeo(null)}
                disabled={reprocessing.all}
                data-testid="reprocess-geo-all-btn"
                title="Reprocessar geolocalização de todos os apoiadores da plataforma"
              >
                <RefreshCcw className={`h-4 w-4 mr-2 ${reprocessing.all ? 'animate-spin' : ''}`} />
                {reprocessing.all ? 'Reprocessando...' : 'Reprocessar geo'}
              </Button>
              <Button onClick={() => navigate('/platform/plans')} variant="outline" data-testid="manage-plans-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Planos
              </Button>
              <Button onClick={() => navigate('/platform/insights')} variant="outline" data-testid="manage-insights-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Insights
              </Button>
              <Button onClick={() => navigate('/platform/stripe')} variant="outline" data-testid="manage-stripe-btn">
                <CreditCard className="h-4 w-4 mr-2" /> Stripe
              </Button>
              <Button onClick={() => navigate('/platform/tenants/new')} data-testid="create-tenant-btn">
                <Plus className="h-4 w-4 mr-2" /> Novo cliente
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-slate-500">Carregando...</div>
            ) : tenants.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-lg">
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium mb-1">Nenhum cliente cadastrado</p>
                <p className="text-sm text-slate-500 mb-4">Crie o primeiro cliente para começar.</p>
                <Button onClick={() => navigate('/platform/tenants/new')}>
                  <Plus className="h-4 w-4 mr-2" /> Criar cliente
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-2">Cliente</th>
                      <th className="py-3 px-2">Slug</th>
                      <th className="py-3 px-2">Status</th>
                      <th className="py-3 px-2 text-center">Lideranças</th>
                      <th className="py-3 px-2 text-center">Links</th>
                      <th className="py-3 px-2 text-center">Redirects</th>
                      <th className="py-3 px-2 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tenant-row-${t.slug}`}>
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-3">
                            <div
                              className="h-9 w-9 rounded-lg flex items-center justify-center font-bold text-white text-sm"
                              style={{ background: t.primary_color || '#1e3a8a' }}
                            >
                              {t.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">{t.name}</div>
                              <div className="text-xs text-slate-500">{t.plan}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-2">
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">{t.slug}</code>
                        </td>
                        <td className="py-4 px-2">{statusBadge(t.status)}</td>
                        <td className="py-4 px-2 text-center font-medium text-slate-700">{t.stats?.leaders || 0}</td>
                        <td className="py-4 px-2 text-center font-medium text-slate-700">{t.stats?.links || 0}</td>
                        <td className="py-4 px-2 text-center font-medium text-slate-700">{t.stats?.total_redirecionamentos || 0}</td>
                        <td className="py-4 px-2">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant={t.insights_enabled ? 'default' : 'outline'}
                              onClick={() => handleToggleInsights(t)}
                              title={t.insights_enabled ? 'Insights habilitado (clique para desabilitar)' : 'Insights desabilitado (clique para habilitar)'}
                              className={t.insights_enabled ? 'bg-emerald-600 hover:bg-emerald-700 h-8' : 'h-8'}
                              data-testid={`toggle-insights-${t.slug}`}
                            >
                              <Sparkles className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleStatus(t)}
                              data-testid={`toggle-status-${t.slug}`}
                            >
                              {t.status === 'active' ? <PauseCircle className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700" data-testid={`delete-tenant-${t.slug}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2">
                                    <AlertCircle className="h-5 w-5 text-rose-600" />
                                    Excluir cliente?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Isso excluirá <strong>{t.name}</strong> e todos os dados associados (lideranças, links, apoiadores, usuários, etc). Esta ação é irreversível.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-rose-600 hover:bg-rose-700"
                                    onClick={() => handleDelete(t)}
                                  >
                                    Excluir definitivamente
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleReprocessGeo(t)}
                              disabled={!!reprocessing.tenants[t.id]}
                              data-testid={`reprocess-geo-${t.slug}`}
                              title="Reprocessar geolocalização dos apoiadores deste cliente"
                            >
                              <RefreshCcw className={`h-4 w-4 ${reprocessing.tenants[t.id] ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleEnter(t)}
                              disabled={t.status !== 'active'}
                              data-testid={`enter-tenant-${t.slug}`}
                            >
                              Entrar <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, total, accent }) => {
  const colors = {
    indigo: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    sky: 'bg-sky-50 text-sky-700 border-sky-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center border ${colors[accent]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="text-3xl font-black text-slate-900 tabular-nums">
        {value.toLocaleString('pt-BR')}
        {total !== undefined && total !== null && (
          <span className="text-sm font-medium text-slate-400 ml-2">/ {total}</span>
        )}
      </div>
    </div>
  );
};
